import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Loader2, Search, Filter } from "lucide-react";
import CustomPackageCard from "@/components/CustomPackageCard";
import { getSupabaseClient, isSupabaseInitialized } from "@/integrations/supabase/client";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { toast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { CustomPackage } from "@/types/packages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";

const CustomPackages = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch custom packages from Supabase
  const { data, isLoading, error } = useQuery({
    queryKey: ["customPackages", currentPage, priceFilter, searchQuery],
    queryFn: async (): Promise<{ packages: CustomPackage[]; count: number }> => {
      try {
        if (!isSupabaseInitialized()) {
          console.error("Supabase client not initialized in CustomPackages component");
          toast({
            title: "Connection Error",
            description: "Database connection issue. Try refreshing the page.",
            variant: "destructive"
          });
          throw new Error("Database connection not initialized");
        }
        
        // First, fetch the custom packages
        let query = (await getSupabaseClient())
          .from("custom_packages")
          .select('*, features:package_features(*), seller_profile:seller_profiles(*)', { count: 'exact' })
          .eq('is_active', true)
          .order("price_cents", { ascending: true })
          .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

        // Add price filter if not "all"
        if (priceFilter === "low") {
          query = query.lt("price_cents", 150000);
        } else if (priceFilter === "medium") {
          query = query.gte("price_cents", 150000).lte("price_cents", 250000);
        } else if (priceFilter === "high") {
          query = query.gt("price_cents", 250000);
        }

        // Add search query if exists
        if (searchQuery) {
          query = query.or(`title.ilike.%${searchQuery}%, description.ilike.%${searchQuery}%`);
        }

        console.log("CustomPackages: Executing query...");
        const { data, error, count } = await query;

        if (error) {
          console.error("Error fetching custom packages:", error);
          console.error("Error details:", JSON.stringify(error));
          toast({
            title: "Error",
            description: "Failed to load custom packages",
            variant: "destructive",
          });
          throw error;
        }
        
        if (!data || data.length === 0) {
          return { packages: [], count: 0 };
        }
        
        console.log("CustomPackages: Data received:", data.length, "items");
        
        // The data already has the features included via the join
        return { 
          packages: data as CustomPackage[], 
          count: count || data.length 
        };
      } catch (err) {
        console.error("Error in query function:", err);
        console.error("Error stack:", err instanceof Error ? err.stack : "No stack available");
        throw err;
      }
    },
    retry: 2,
  });

  // Calculate total pages
  const totalPages = Math.ceil((data?.count || 0) / itemsPerPage);

  return (
    <>
      <Helmet>
        <title>Custom Packages | BackLink Hub</title>
      </Helmet>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Custom Packages</h1>
            <p className="text-muted-foreground">
              Explore premium custom packages tailored for specific needs
            </p>
          </div>
          
          {user && (
            <Button asChild>
              <Link to="/create-custom-package">Create Custom Package</Link>
            </Button>
          )}
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative w-full md:w-1/2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search packages..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select value={priceFilter} onValueChange={setPriceFilter}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Price Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Prices</SelectItem>
              <SelectItem value="low">Under $1,500</SelectItem>
              <SelectItem value="medium">$1,500 - $2,500</SelectItem>
              <SelectItem value="high">Over $2,500</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator className="mb-6" />

        {/* Packages Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading packages...</span>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-red-500">Error loading packages. Please try again later.</p>
              <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : data?.packages?.length === 0 ? (
          <EmptyState
            title="No custom packages found"
            description="Try adjusting your search or filters, or check back later"
            icon="inbox"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {data?.packages.map((packageItem) => (
                <CustomPackageCard 
                  key={packageItem.id}
                  package={packageItem}
                />
              ))}
            </div>
            
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CustomPackages; 